import React, { useState, useMemo, useRef } from 'react';
import Draggable from 'react-draggable';
import { DataTableColumn } from '../../../DataTable.types';
import styles from './ColumnDragList.module.scss';
import { PinnedColumnIcon, DragHandleIcon, MoveToTopIcon } from './ColumnDragListIcons';
import { crossBtnGrey } from '../../../../../../assets/hostedassets';

interface ColumnDragListProps {
    columns: string[];
    columnMetadataMap: Map<string, DataTableColumn>;
    pinnedColumns?: { field: string; position: 'left' | 'right' }[];
    onMoveToTop: (columnId: string) => void;
    onRemove: (columnId: string) => void;
    onReorder: (reorderedColumns: string[]) => void;
}

const ColumnDragList: React.FC<ColumnDragListProps> = ({
    columns,
    columnMetadataMap,
    pinnedColumns = [],
    onMoveToTop,
    onRemove,
    onReorder,
}) => {
    const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);
    const [draggedItem, setDraggedItem] = useState<string | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Create set of pinned column fields for quick lookup
    const pinnedColumnsSet = useMemo(() => new Set(pinnedColumns.map((pc) => pc.field)), [pinnedColumns]);

    // Separate left and right pinned columns
    const leftPinnedColumns = useMemo(() => pinnedColumns.filter((pc) => pc.position === 'left').map((pc) => pc.field), [pinnedColumns]);
    const rightPinnedColumns = useMemo(() => pinnedColumns.filter((pc) => pc.position === 'right').map((pc) => pc.field), [pinnedColumns]);

    const handleMoveToTop = (columnId: string) => {
        // Don't allow moving pinned columns
        if (pinnedColumnsSet.has(columnId)) {
            return;
        }
        onMoveToTop(columnId);
    };

    const handleRemove = (columnId: string) => {
        // Don't allow removing pinned columns
        if (pinnedColumnsSet.has(columnId)) {
            return;
        }
        onRemove(columnId);
    };

    const handleMouseEnter = (columnId: string) => {
        setHoveredColumn(columnId);
    };

    const handleMouseLeave = () => {
        setHoveredColumn(null);
    };

    const handleDragStart = (columnId: string) => {
        // Don't allow dragging pinned columns
        if (pinnedColumnsSet.has(columnId)) {
            return;
        }
        setDraggedItem(columnId);
    };

    const handleDrag = (columnId: string, data: any) => {
        if (!containerRef.current) return;

        const currentIndex = columns.indexOf(columnId);
        const relativeY = data.y;

        // Only update highlighting if the user has moved a meaningful distance
        const dragThreshold = 10; // Minimum pixels to move before showing highlighting
        if (Math.abs(relativeY) < dragThreshold) {
            return;
        }

        // Calculate the actual item height by measuring the container
        const containerHeight = containerRef.current.offsetHeight;
        const itemHeight = containerHeight / columns.length;

        // Calculate the target position based on the current position + relative movement
        const targetPosition = currentIndex + relativeY / itemHeight;
        const rawIndex = Math.max(0, Math.min(columns.length - 1, Math.round(targetPosition)));

        // Apply smart positioning logic respecting pinned column positions
        let targetIndex = rawIndex;
        const targetColumn = columns[rawIndex];

        if (pinnedColumnsSet.has(targetColumn)) {
            // If target is a left pinned column, place at the end of left pinned columns
            if (leftPinnedColumns.includes(targetColumn)) {
                targetIndex = leftPinnedColumns.length;
            }
            // If target is a right pinned column, place at the beginning of right pinned columns
            else if (rightPinnedColumns.includes(targetColumn)) {
                targetIndex = columns.length - rightPinnedColumns.length;
            }
            // For other pinned columns, use the original logic
            else {
                if (rawIndex < currentIndex) {
                    targetIndex = rawIndex;
                } else {
                    targetIndex = rawIndex + 1;
                }
            }
        } else {
            // For regular columns, allow normal positioning but respect boundaries
            const leftPinnedCount = leftPinnedColumns.length;
            const rightPinnedCount = rightPinnedColumns.length;

            // Ensure target index is within the valid range for regular columns
            targetIndex = Math.max(leftPinnedCount, Math.min(columns.length - rightPinnedCount - 1, rawIndex));
        }

        // Ensure the target index is within bounds
        targetIndex = Math.max(0, Math.min(columns.length - 1, targetIndex));

        // Update drag over index for visual feedback
        if (targetIndex !== dragOverIndex) {
            setDragOverIndex(targetIndex);
        }
    };

    const handleDragStop = (columnId: string, data: any) => {
        if (!containerRef.current) return;

        const currentIndex = columns.indexOf(columnId);
        const relativeY = data.y;

        // Calculate the actual item height by measuring the container
        const containerHeight = containerRef.current.offsetHeight;
        const itemHeight = containerHeight / columns.length;

        // Calculate the target position based on the current position + relative movement
        const targetPosition = currentIndex + relativeY / itemHeight;
        const rawIndex = Math.max(0, Math.min(columns.length - 1, Math.round(targetPosition)));

        // Apply smart positioning logic respecting pinned column positions
        let targetIndex = rawIndex;
        const targetColumn = columns[rawIndex];

        if (pinnedColumnsSet.has(targetColumn)) {
            // If target is a left pinned column, place at the end of left pinned columns
            if (leftPinnedColumns.includes(targetColumn)) {
                targetIndex = leftPinnedColumns.length;
            }
            // If target is a right pinned column, place at the beginning of right pinned columns
            else if (rightPinnedColumns.includes(targetColumn)) {
                targetIndex = columns.length - rightPinnedColumns.length;
            }
            // For other pinned columns, use the original logic
            else {
                if (rawIndex < currentIndex) {
                    targetIndex = rawIndex;
                } else {
                    targetIndex = rawIndex + 1;
                }
            }
        } else {
            // For regular columns, allow normal positioning but respect boundaries
            const leftPinnedCount = leftPinnedColumns.length;
            const rightPinnedCount = rightPinnedColumns.length;

            // Ensure target index is within the valid range for regular columns
            targetIndex = Math.max(leftPinnedCount, Math.min(columns.length - rightPinnedCount - 1, rawIndex));
        }

        // Ensure the target index is within bounds
        targetIndex = Math.max(0, Math.min(columns.length - 1, targetIndex));

        // Only reorder if the index actually changed
        if (targetIndex !== currentIndex && targetIndex < columns.length) {
            const newOrder = [...columns];
            const [removed] = newOrder.splice(currentIndex, 1);
            newOrder.splice(targetIndex, 0, removed);
            onReorder(newOrder);
        }

        // Reset drag state
        setDraggedItem(null);
        setDragOverIndex(null);
    };

    return (
        <div ref={containerRef} className={styles.DragList}>
            {columns
                .map((columnId, index) => {
                    const column = columnMetadataMap.get(columnId);
                    return { columnId, index, column };
                })
                .filter((item): item is { columnId: string; index: number; column: DataTableColumn } => item.column !== undefined)
                .map(({ columnId, index, column }) => {
                    const isPinned = pinnedColumnsSet.has(columnId);
                    const pinnedInfo = pinnedColumns.find((pc) => pc.field === columnId);

                    // Check if this is the first regular column (right after pinned columns)
                    const allPinnedColumns = [...leftPinnedColumns, ...rightPinnedColumns];
                    let pinnedCount = 0;
                    for (let i = 0; i < columns.length; i++) {
                        if (allPinnedColumns.includes(columns[i])) {
                            pinnedCount++;
                        } else {
                            break;
                        }
                    }
                    const isFirstRegularColumn = index === pinnedCount;

                    const showMoveToTop = hoveredColumn === columnId && !isPinned && !isFirstRegularColumn;
                    const isDragging = draggedItem === columnId;
                    const isDragOver = dragOverIndex === index;

                    const dragItem = (
                        <div
                            className={`${styles.DragItem} ${isDragging ? styles.Dragging : ''} ${isPinned ? styles.PinnedItem : ''} ${
                                isDragOver ? styles.DragOver : ''
                            }`}
                            onMouseEnter={() => handleMouseEnter(columnId)}
                            onMouseLeave={handleMouseLeave}
                        >
                            <div className={`${styles.DragHandle} ${isPinned ? styles.DisabledHandle : ''}`}>
                                <DragHandleIcon />
                            </div>
                            <span className={`${styles.ColumnName} ${isPinned ? styles.PinnedColumnName : ''}`}>{column.headerLabel}</span>
                            <div className={styles.ActionButtons}>
                                {showMoveToTop && (
                                    <button
                                        className={styles.MoveToTopButton}
                                        onClick={() => handleMoveToTop(columnId)}
                                        title="Move to top"
                                    >
                                        Move to top
                                    </button>
                                )}
                                {!isPinned && (
                                    <button className={styles.RemoveButton} onClick={() => handleRemove(columnId)} title="Remove column">
                                        <img src={crossBtnGrey} alt="Remove" width="16" height="16" />
                                    </button>
                                )}
                            </div>
                        </div>
                    );

                    // If pinned, render without draggable wrapper
                    if (isPinned) {
                        return <div key={columnId}>{dragItem}</div>;
                    }

                    // For non-pinned items, wrap with Draggable
                    return (
                        <Draggable
                            key={columnId}
                            axis="y"
                            position={{ x: 0, y: 0 }}
                            onStart={() => handleDragStart(columnId)}
                            onDrag={(e, data) => handleDrag(columnId, data)}
                            onStop={(e, data) => handleDragStop(columnId, data)}
                            bounds="parent"
                        >
                            {dragItem}
                        </Draggable>
                    );
                })}
        </div>
    );
};

export default ColumnDragList;
